/**
 * Super admin session + API client.
 *
 * The credentials themselves live in backend code (app/config.py, overridable
 * per environment) — they are NOT in this bundle. Signing in exchanges them for
 * a short-lived token with type "super_admin", and that token is what authorizes
 * every org write. The browser-side flag this module keeps is routing UX only:
 * clearing it locks the console, but the API is what actually protects the data.
 *
 * Deliberately separate from lib/api.ts: that client sends the portal user's
 * token from localStorage["token"] and, on a 401, wipes it and redirects to
 * /sign-in. A super admin request must never do either of those things.
 */
const TOKEN_KEY = "super_admin_token";
const EMAIL_KEY = "super_admin_email";

const API_BASE = import.meta.env.VITE_API_BASE_URL || "http://localhost:8000/api/v1";

export class SuperAdminApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "SuperAdminApiError";
    this.status = status;
  }
}

/** Tab-scoped, so closing the tab ends the session. */
function read(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

export function getSuperAdminToken(): string | null {
  return read(TOKEN_KEY);
}

export function getSuperAdminEmail(): string | null {
  return read(EMAIL_KEY);
}

export function isSuperAdminSignedIn(): boolean {
  return Boolean(getSuperAdminToken());
}

export function endSuperAdminSession(): void {
  try {
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(EMAIL_KEY);
  } catch {
    /* storage unavailable — nothing to clean up */
  }
}

/** Exchange credentials for a token. Throws with the server's message on failure.
 *  `captcha` carries the bot-check fields from useCaptcha().payload() — the
 *  endpoint verifies them before it looks at the credentials at all. */
export async function superAdminLogin(
  email: string,
  password: string,
  captcha?: { captcha_token?: string; captcha_answer?: string; recaptcha_token?: string },
): Promise<void> {
  const res = await fetch(`${API_BASE}/super-admin/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email: email.trim(), password, ...(captcha ?? {}) }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new SuperAdminApiError(
      res.status,
      typeof body.detail === "string" ? body.detail : "Invalid super admin credentials",
    );
  }
  const data: { access_token: string; email: string } = await res.json();
  try {
    sessionStorage.setItem(TOKEN_KEY, data.access_token);
    sessionStorage.setItem(EMAIL_KEY, data.email);
  } catch {
    /* private mode — the session just won't survive a reload */
  }
}

async function request<T>(path: string, options?: RequestInit, isMultipart = false): Promise<T> {
  const token = getSuperAdminToken();
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      // Multipart must NOT set Content-Type by hand — the browser has to add
      // the boundary parameter itself or the server can't parse the body.
      ...(isMultipart ? {} : { "Content-Type": "application/json" }),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options?.headers,
    },
  });

  // Super admin tokens DO expire (unlike portal tokens). A 401 means this
  // session is over — drop it and send them back to the super admin door, never
  // the portal one.
  if (res.status === 401) {
    endSuperAdminSession();
    if (!window.location.pathname.startsWith("/super-admin/sign-in")) {
      window.location.assign("/super-admin/sign-in");
    }
    throw new SuperAdminApiError(401, "Your super admin session expired. Please sign in again.");
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const detail = body.detail;
    throw new SuperAdminApiError(
      res.status,
      typeof detail === "string"
        ? detail
        : Array.isArray(detail) && detail[0]?.msg
          ? String(detail[0].msg)
          : `Request failed (${res.status})`,
    );
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

export const superAdminApi = {
  get: <T,>(path: string) => request<T>(path),
  post: <T,>(path: string, body: unknown) =>
    request<T>(path, { method: "POST", body: JSON.stringify(body) }),
  put: <T,>(path: string, body: unknown) =>
    request<T>(path, { method: "PUT", body: JSON.stringify(body) }),
  patch: <T,>(path: string, body: unknown) =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(body) }),
  delete: <T,>(path: string) => request<T>(path, { method: "DELETE" }),
  // Signature matches lib/api.ts's upload so the same page component can run on
  // either client — the resource console posts a `kind` field alongside the file.
  upload: <T,>(path: string, file: File, fieldName = "file", fields?: Record<string, string>) => {
    const form = new FormData();
    form.append(fieldName, file);
    if (fields) for (const [k, v] of Object.entries(fields)) form.append(k, v);
    return request<T>(path, { method: "POST", body: form }, true);
  },
};

/** An admin account belonging to an organization. */
export interface OrgAdmin {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  status: string;
  org_id: number;
  created_at: string;
}

/** An organization as returned by /orgs. */
export interface Org {
  id: number;
  name: string;
  slug: string;
  is_default: boolean;
  is_active: boolean;
  url_path: string;
  headline: string | null;
  subheadline: string | null;
  description: string | null;
  logo_url: string | null;
  website_url: string | null;
  tier_names: string[];
}
